import React, { useState, useEffect } from "react";
import DashboardHeader from "./components/DashboardHeader";
import StatsSection from "./components/StatsSection";
import SearchAndFilters from "./components/SearchAndFilters";
import PatientTable from "./components/PatientTable";
import PatientFormModal from "./components/PatientFormModal";
import PatientDetailsModal from "./components/PatientDetailsModal";
import DeleteConfirmationModal from "./components/DeleteConfirmationModal";
import {
  getPatients,
  addPatient,
  updatePatient,
  deletePatient,
} from "../api/apiFunctions";
import { getAuthToken } from "../utils/auth";

const DoctorDashboard = ({ lang = "en" }) => {
  // Patient data from API
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State management
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [showAddPatientModal, setShowAddPatientModal] = useState(false);
  const [showPatientDetailsModal, setShowPatientDetailsModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [editingPatient, setEditingPatient] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientToDelete, setPatientToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [patientsPerPage] = useState(5);
  const [patientForm, setPatientForm] = useState({
    fullName: "",
    email: "",
    ssn: "",
    memberId: "",
    password: "",
    primaryPhone: "",
    mobilePhone: "",
    address1: "",
    address2: "",
    city: "",
    postalCode: "",
    state: "",
    country: "",
    dob: "",
    height: "",
    weight: "",
    insurance: "",
    policy: "",
    gender: "",
    bloodType: "",
    nationality: "",
    ethnicity: "",
    organDonor: false,
    emergencyName: "",
    emergencyPhone: "",
    emergencyRelation: "",
    religion: "",
    placeOfWorship: "",
    placeOfWorshipAddress: "",
    maritalStatus: "",
    workStatus: "",
    education: "",
    occupation: "",
    dietaryRoutine: "",
    exerciseFrequency: "",
    // Medical Information
    conditionAlias: "",
    conditionMoreInfo: "",
    procedureFullName: "",
    medicalCode: "",
    medicalCodeDotted: "",
    familyConditionAlias: "",
    familyConditionMoreInfo: "",
    // Arrays for multiple entries
    allergies: [],
    medications: [],
    conditions: [],
    procedures: [],
    familyHistory: [],
    emailAlerts: false,
    smsNotifications: false,
    newsletter: false,
  });

  // Load patients from API
  const loadPatients = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = getAuthToken();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await getPatients(token);

      if (response.success) {
        // Handle different possible response structures
        let patientsData = response.data || response.patients || response;

        // If it's not an array, try to extract the array
        if (!Array.isArray(patientsData)) {
          if (patientsData.patients && Array.isArray(patientsData.patients)) {
            patientsData = patientsData.patients;
          } else if (patientsData.data && Array.isArray(patientsData.data)) {
            patientsData = patientsData.data;
          } else {
            console.error("Unexpected response structure:", patientsData);
            throw new Error("Invalid response format from server");
          }
        }

        // Ensure we have an array to work with
        if (!Array.isArray(patientsData)) {
          console.warn("Patients data is not an array:", patientsData);
          setPatients([]);
          return;
        }

        // Transform API data to match the expected format
        const transformedPatients = patientsData.map((patient) => ({
          id: patient._id || patient.id,
          fullName: patient.name || patient.fullName,
          email: patient.email,
          memberId: patient.memberId,
          dob: patient.dateOfBirth || patient.dob,
          primaryPhone: patient.primaryPhoneNumber || patient.primaryPhone,
          address1: patient.addressLine1 || patient.address1,
          city: patient.city,
          state: patient.state,
          postalCode: patient.postalCode,
          gender: patient.gender,
          bloodType: patient.bloodType,
          insurance: patient.primaryInsurance || patient.insurance,
          policy: patient.policy,
          emergencyName:
            patient.emergencyContact?.name || patient.emergencyName,
          emergencyPhone:
            patient.emergencyContact?.phone || patient.emergencyPhone,
          emergencyRelation:
            patient.emergencyContact?.relationship || patient.emergencyRelation,
          createdAt: patient.createdAt,
          status: patient.isActive ? "active" : "inactive",
        }));

        setPatients(transformedPatients);
      } else {
        // If response.success is false, check if there's a message
        if (response.message) {
          throw new Error(response.message);
        } else {
          if (Array.isArray(response)) {
            const transformedPatients = response.map((patient) => ({
              id: patient._id || patient.id,
              fullName: patient.name || patient.fullName,
              email: patient.email,
              memberId: patient.memberId,
              dob: patient.dateOfBirth || patient.dob,
              primaryPhone: patient.primaryPhoneNumber || patient.primaryPhone,
              address1: patient.addressLine1 || patient.address1,
              city: patient.city,
              state: patient.state,
              postalCode: patient.postalCode,
              gender: patient.gender,
              bloodType: patient.bloodType,
              insurance: patient.primaryInsurance || patient.insurance,
              policy: patient.policy,
              emergencyName:
                patient.emergencyContact?.name || patient.emergencyName,
              emergencyPhone:
                patient.emergencyContact?.phone || patient.emergencyPhone,
              emergencyRelation:
                patient.emergencyContact?.relationship ||
                patient.emergencyRelation,
              createdAt: patient.createdAt,
              status: patient.isActive ? "active" : "inactive",
            }));
            setPatients(transformedPatients);
          } else {
            throw new Error("Unexpected response format");
          }
        }
      }
    } catch (err) {
      console.error("Error loading patients:", err);
      setError(err.message || "Failed to load patients");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    loadPatients();
  }, []);

  // Event handlers
  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPatientForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const resetForm = () => {
    setPatientForm({
      fullName: "",
      email: "",
      ssn: "",
      memberId: "",
      password: "",
      primaryPhone: "",
      mobilePhone: "",
      address1: "",
      address2: "",
      city: "",
      postalCode: "",
      state: "",
      country: "",
      dob: "",
      height: "",
      weight: "",
      insurance: "",
      policy: "",
      gender: "",
      bloodType: "",
      nationality: "",
      ethnicity: "",
      organDonor: false,
      emergencyName: "",
      emergencyPhone: "",
      emergencyRelation: "",
      religion: "",
      placeOfWorship: "",
      placeOfWorshipAddress: "",
      maritalStatus: "",
      workStatus: "",
      education: "",
      occupation: "",
      dietaryRoutine: "",
      exerciseFrequency: "",
      // Medical Information
      conditionAlias: "",
      conditionMoreInfo: "",
      procedureFullName: "",
      medicalCode: "",
      medicalCodeDotted: "",
      familyConditionAlias: "",
      familyConditionMoreInfo: "",
      // Arrays for multiple entries
      allergies: [],
      medications: [],
      conditions: [],
      procedures: [],
      familyHistory: [],
      emailAlerts: false,
      smsNotifications: false,
      newsletter: false,
    });
  };

  const handleAddPatient = () => {
    setEditingPatient(null);
    resetForm();
    setError(null); // Clear any previous errors
    setShowAddPatientModal(true);
  };

  const handleEditPatient = (patient) => {
    setEditingPatient(patient);
    setPatientForm(patient);
    setShowAddPatientModal(true);
  };

  const handleViewPatient = (patient) => {
    setSelectedPatient(patient);
    setShowPatientDetailsModal(true);
  };

  const handleDeletePatient = (patient) => {
    setPatientToDelete(patient);
    setShowDeleteConfirmModal(true);
  };

  const confirmDeletePatient = async () => {
    if (patientToDelete) {
      setIsDeleting(true);
      setError(null);

      try {
        const token = getAuthToken();

        if (!token) {
          throw new Error("No authentication token found");
        }

        const response = await deletePatient(patientToDelete.id, token);

        if (response.success) {
          // Update local state directly instead of reloading
          setPatients((prev) =>
            prev.filter((p) => p.id !== patientToDelete.id)
          );
          setShowDeleteConfirmModal(false);
          setPatientToDelete(null);
        } else {
          throw new Error(response.message || "Failed to delete patient");
        }
      } catch (err) {
        console.error("Error deleting patient:", err);

        // Show specific error messages
        let errorMessage = err.message || "Failed to delete patient";

        if (errorMessage.includes("not found")) {
          errorMessage = "Patient not found. It may have already been deleted.";
        } else if (errorMessage.includes("permission")) {
          errorMessage = "You don't have permission to delete this patient.";
        }

        setError(errorMessage);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const handleStatusChange = async (patientId, newStatus) => {
    setIsUpdatingStatus(true);
    setError(null);

    try {
      const token = getAuthToken();

      if (!token) {
        throw new Error("No authentication token found");
      }

      // Find the patient to get their current data
      const patient = patients.find((p) => p.id === patientId);
      if (!patient) {
        throw new Error("Patient not found");
      }

      // Update the patient with new status
      const updateData = {
        fullname: patient.fullName,
        email: patient.email,
        isActive: newStatus === "active",
      };

      const response = await updatePatient(patientId, updateData, token);

      if (response.success) {
        // Update local state directly instead of reloading
        setPatients((prev) =>
          prev.map((p) =>
            p.id === patientId
              ? { ...p, status: newStatus === "active" ? "active" : "inactive" }
              : p
          )
        );
      } else {
        throw new Error(response.message || "Failed to update patient status");
      }
    } catch (err) {
      console.error("Error updating patient status:", err);
      setError(err.message || "Failed to update patient status");
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleSort = () => {
    setShowSortDropdown(!showSortDropdown);
  };

  const handleSortOption = (option, order) => {
    setSortBy(option);
    setSortOrder(order);
    setShowSortDropdown(false);
  };

  const [validationErrors, setValidationErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const validateForm = () => {
    const errors = {};

    if (!patientForm.fullName?.trim()) {
      errors.fullName = "Full name is required";
    }

    if (!patientForm.email?.trim()) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(patientForm.email)) {
      errors.email = "Please enter a valid email address";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSavePatient = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    setError(null); // Clear any previous errors

    try {
      const token = getAuthToken();

      if (!token) {
        throw new Error("No authentication token found");
      }

      if (editingPatient) {
        // Create update data for existing patient
        const updateData = {
          // Basic Information - only include if not empty
          fullname: patientForm.fullName || "",
          email: patientForm.email || "",
          ssn: patientForm.ssn || "",
          memberID: patientForm.memberId || "",
          primaryPhone: patientForm.primaryPhone || "",
          mobilePhone: patientForm.mobilePhone || "",

          // Address
          addressLine1: patientForm.address1 || "",
          addressLine2: patientForm.address2 || "",
          city: patientForm.city || "",
          state: patientForm.state || "",
          postalCode: patientForm.postalCode || "",
          country: patientForm.country || "",

          // Personal & Medical
          dob: patientForm.dob || "",
          height: patientForm.height || "",
          weight: patientForm.weight || "",
          gender: patientForm.gender || "",
          bloodtype: patientForm.bloodType || "",
          nationality: patientForm.nationality || "",
          ethnicity: patientForm.ethnicity || "",
          organdonor: patientForm.organDonor === "true" || patientForm.organDonor === true,

          // Insurance
          insuranceProvider: patientForm.insurance || "",
          policyNumber: patientForm.policy || "",

          // Medications (multiple) - ensure arrays are properly formatted
          medications: Array.isArray(patientForm.medications)
            ? patientForm.medications
            : [],
          dosage: Array.isArray(patientForm.dosage) ? patientForm.dosage : [],
          notes: Array.isArray(patientForm.notes) ? patientForm.notes : [],

          // Allergies (multiple)
          allergies: Array.isArray(patientForm.allergies)
            ? patientForm.allergies
            : [],
          allergiesNotes: Array.isArray(patientForm.allergiesNotes)
            ? patientForm.allergiesNotes
            : [],

          // Medical Conditions (multiple)
          medicalConditions: Array.isArray(patientForm.conditions)
            ? patientForm.conditions
            : [],
          medicalConditionsNotes: Array.isArray(patientForm.conditionMoreInfo)
            ? patientForm.conditionMoreInfo
            : [],
          alias: Array.isArray(patientForm.conditionAlias)
            ? patientForm.conditionAlias
            : [],
          moreInfo: Array.isArray(patientForm.conditionMoreInfo)
            ? patientForm.conditionMoreInfo
            : [],

          // Medical Procedures (multiple)
          medicalProcedure: Array.isArray(patientForm.procedures)
            ? patientForm.procedures
            : [],
          medicalProcedureNotes: Array.isArray(patientForm.procedureMoreInfo)
            ? patientForm.procedureMoreInfo
            : [],
          medicalProcedureFullname: Array.isArray(patientForm.procedureFullName)
            ? patientForm.procedureFullName
            : [],
          medicalCode: Array.isArray(patientForm.medicalCode)
            ? patientForm.medicalCode
            : [],
          dotted: Array.isArray(patientForm.medicalCodeDotted)
            ? patientForm.medicalCodeDotted
            : [],

          // Family History (multiple)
          familyHistoryCondition: Array.isArray(patientForm.familyHistory)
            ? patientForm.familyHistory
            : [],
          relationship: Array.isArray(patientForm.familyRelationships)
            ? patientForm.familyRelationships
            : [],
          familyHistoryAlias: Array.isArray(patientForm.familyConditionAlias)
            ? patientForm.familyConditionAlias
            : [],
          familyHistoryMoreInfo: Array.isArray(
            patientForm.familyConditionMoreInfo
          )
            ? patientForm.familyConditionMoreInfo
            : [],
          familyHistoryNotes: Array.isArray(patientForm.familyNotes)
            ? patientForm.familyNotes
            : [],

          // Lifestyle
          religion: patientForm.religion || "",
          worshipPlace: patientForm.placeOfWorship || "",
          worshipAddress: patientForm.placeOfWorshipAddress || "",
          maritalStatus: patientForm.maritalStatus || "",
          workStatus: patientForm.workStatus || "",
          education: patientForm.education || "",
          occupation: patientForm.occupation || "",
          dietaryRoutine: patientForm.dietaryRoutine || "",
          exerciseFrequency: patientForm.exerciseFrequency || "",

          // Emergency Contact
          emergencyContactName: patientForm.emergencyName || "",
          emergencyContactPhone: patientForm.emergencyPhone || "",
          emergencyRelationship: patientForm.emergencyRelation || "",
        };

        // Validate required fields before sending
        if (!updateData.fullname || !updateData.email) {
          throw new Error("Full name and email are required");
        }

        const response = await updatePatient(
          editingPatient.id,
          updateData,
          token
        );

        if (response.success) {
          // Update local state directly instead of reloading
          setPatients((prev) =>
            prev.map((p) =>
              p.id === editingPatient.id
                ? {
                    ...p,
                    fullName: updateData.fullname,
                    email: updateData.email,
                    memberId: updateData.memberID,
                    dob: updateData.dob,
                    primaryPhone: updateData.primaryPhone,
                    mobilePhone: updateData.mobilePhone,
                    address1: updateData.addressLine1,
                    address2: updateData.addressLine2,
                    city: updateData.city,
                    state: updateData.state,
                    postalCode: updateData.postalCode,
                    country: updateData.country,
                    gender: updateData.gender,
                    bloodType: updateData.bloodtype,
                    nationality: updateData.nationality,
                    ethnicity: updateData.ethnicity,
                    organDonor: updateData.organdonor,
                    height: updateData.height,
                    weight: updateData.weight,
                    insurance: updateData.insuranceProvider,
                    policy: updateData.policyNumber,
                    emergencyName: updateData.emergencyContactName,
                    emergencyPhone: updateData.emergencyContactPhone,
                    emergencyRelation: updateData.emergencyRelationship,
                  }
                : p
            )
          );
        } else {
          throw new Error(response.message || "Failed to update patient");
        }
      } else {
        // Transform form data to match API expected format
        const apiPatientData = {
          // Basic Information - only include if not empty
          fullname: patientForm.fullName || "",
          email: patientForm.email || "",
          ssn: patientForm.ssn || "",
          memberID: patientForm.memberId || "",
          primaryPhone: patientForm.primaryPhone || "",
          mobilePhone: patientForm.mobilePhone || "",

          // Address
          addressLine1: patientForm.address1 || "",
          addressLine2: patientForm.address2 || "",
          city: patientForm.city || "",
          state: patientForm.state || "",
          postalCode: patientForm.postalCode || "",
          country: patientForm.country || "",

          // Personal & Medical
          dob: patientForm.dob || "",
          height: patientForm.height || "",
          weight: patientForm.weight || "",
          gender: patientForm.gender || "",
          bloodtype: patientForm.bloodType || "",
          nationality: patientForm.nationality || "",
          ethnicity: patientForm.ethnicity || "",
          organdonor: patientForm.organDonor === "true" || patientForm.organDonor === true,

          // Insurance
          insuranceProvider: patientForm.insurance || "",
          policyNumber: patientForm.policy || "",

          // Medications (multiple) - ensure arrays are properly formatted
          medications: Array.isArray(patientForm.medications)
            ? patientForm.medications
            : [],
          dosage: Array.isArray(patientForm.dosage) ? patientForm.dosage : [],
          notes: Array.isArray(patientForm.notes) ? patientForm.notes : [],

          // Allergies (multiple)
          allergies: Array.isArray(patientForm.allergies)
            ? patientForm.allergies
            : [],
          allergiesNotes: Array.isArray(patientForm.allergiesNotes)
            ? patientForm.allergiesNotes
            : [],

          // Medical Conditions (multiple)
          medicalConditions: Array.isArray(patientForm.conditions)
            ? patientForm.conditions
            : [],
          medicalConditionsNotes: Array.isArray(patientForm.conditionMoreInfo)
            ? patientForm.conditionMoreInfo
            : [],
          alias: Array.isArray(patientForm.conditionAlias)
            ? patientForm.conditionAlias
            : [],
          moreInfo: Array.isArray(patientForm.conditionMoreInfo)
            ? patientForm.conditionMoreInfo
            : [],

          // Medical Procedures (multiple)
          medicalProcedure: Array.isArray(patientForm.procedures)
            ? patientForm.procedures
            : [],
          medicalProcedureNotes: Array.isArray(patientForm.procedureMoreInfo)
            ? patientForm.procedureMoreInfo
            : [],
          medicalProcedureFullname: Array.isArray(patientForm.procedureFullName)
            ? patientForm.procedureFullName
            : [],
          medicalCode: Array.isArray(patientForm.medicalCode)
            ? patientForm.medicalCode
            : [],
          dotted: Array.isArray(patientForm.medicalCodeDotted)
            ? patientForm.medicalCodeDotted
            : [],

          // Family History (multiple)
          familyHistoryCondition: Array.isArray(patientForm.familyHistory)
            ? patientForm.familyHistory
            : [],
          relationship: Array.isArray(patientForm.familyRelationships)
            ? patientForm.familyRelationships
            : [],
          familyHistoryAlias: Array.isArray(patientForm.familyConditionAlias)
            ? patientForm.familyConditionAlias
            : [],
          familyHistoryMoreInfo: Array.isArray(
            patientForm.familyConditionMoreInfo
          )
            ? patientForm.familyConditionMoreInfo
            : [],
          familyHistoryNotes: Array.isArray(patientForm.familyNotes)
            ? patientForm.familyNotes
            : [],

          // Lifestyle
          religion: patientForm.religion || "",
          worshipPlace: patientForm.placeOfWorship || "",
          worshipAddress: patientForm.placeOfWorshipAddress || "",
          maritalStatus: patientForm.maritalStatus || "",
          workStatus: patientForm.workStatus || "",
          education: patientForm.education || "",
          occupation: patientForm.occupation || "",
          dietaryRoutine: patientForm.dietaryRoutine || "",
          exerciseFrequency: patientForm.exerciseFrequency || "",

          // Emergency Contact
          emergencyContactName: patientForm.emergencyName || "",
          emergencyContactPhone: patientForm.emergencyPhone || "",
          emergencyRelationship: patientForm.emergencyRelation || "",
        };

        // Validate required fields before sending
        if (!apiPatientData.fullname || !apiPatientData.email) {
          throw new Error("Full name and email are required");
        }

        // Create a minimal test payload first
        const testPayload = {
          fullname: apiPatientData.fullname,
          email: apiPatientData.email,
          // Add other required fields with defaults
          ssn: "",
          memberID: "",
          primaryPhone: "",
          mobilePhone: "",
          addressLine1: "",
          addressLine2: "",
          city: "",
          state: "",
          postalCode: "",
          country: "",
          dob: "",
          height: "",
          weight: "",
          gender: "",
          bloodtype: "",
          nationality: "",
          ethnicity: "",
          organdonor: false,
          insuranceProvider: "",
          policyNumber: "",
          medications: [],
          dosage: [],
          notes: [],
          allergies: [],
          allergiesNotes: [],
          medicalConditions: [],
          medicalConditionsNotes: [],
          alias: [],
          moreInfo: [],
          medicalProcedure: [],
          medicalProcedureNotes: [],
          medicalProcedureFullname: [],
          medicalCode: [],
          dotted: [],
          familyHistoryCondition: [],
          relationship: [],
          familyHistoryAlias: [],
          familyHistoryMoreInfo: [],
          familyHistoryNotes: [],
          religion: "",
          worshipPlace: "",
          worshipAddress: "",
          maritalStatus: "",
          workStatus: "",
          education: "",
          occupation: "",
          dietaryRoutine: "",
          exerciseFrequency: "",
          emergencyContactName: "",
          emergencyContactPhone: "",
          emergencyRelationship: "",
        };

        const response = await addPatient(testPayload, token);

        if (response.success) {
          // Add new patient to local state instead of reloading
          const newPatient = {
            id: response.data?.patientId || Date.now().toString(),
            fullName: testPayload.fullname,
            email: testPayload.email,
            memberId: response.data?.memberId || testPayload.memberID,
            dob: testPayload.dob,
            primaryPhone: testPayload.primaryPhone,
            mobilePhone: testPayload.mobilePhone,
            address1: testPayload.addressLine1,
            address2: testPayload.addressLine2,
            city: testPayload.city,
            state: testPayload.state,
            postalCode: testPayload.postalCode,
            country: testPayload.country,
            gender: testPayload.gender,
            bloodType: testPayload.bloodtype,
            nationality: testPayload.nationality,
            ethnicity: testPayload.ethnicity,
            organDonor: testPayload.organdonor,
            height: testPayload.height,
            weight: testPayload.weight,
            insurance: testPayload.insuranceProvider,
            policy: testPayload.policyNumber,
            emergencyName: testPayload.emergencyContactName,
            emergencyPhone: testPayload.emergencyContactPhone,
            emergencyRelation: testPayload.emergencyRelationship,
            createdAt: new Date().toISOString(),
            status: "active",
          };

          setPatients((prev) => [...prev, newPatient]);
        } else {
          throw new Error(response.message || "Failed to add patient");
        }
      }

      setShowAddPatientModal(false);
      setEditingPatient(null);
      setValidationErrors({});
      resetForm();
    } catch (err) {
      console.error("Error saving patient:", err);

      // Show specific error messages to help the doctor fix the issue
      let errorMessage = err.message || "Failed to save patient";

      // Handle specific validation errors
      if (errorMessage.includes("Email already exists")) {
        errorMessage =
          "This email address is already registered. Please use a different email address.";
      } else if (errorMessage.includes("Validation error")) {
        errorMessage =
          "Please check the form data and ensure all required fields are filled correctly.";
      } else if (errorMessage.includes("Full name and email are required")) {
        errorMessage =
          "Please fill in the patient's full name and email address.";
      }

      setError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  // Filtering and sorting logic
  const filteredPatients = patients
    .filter((patient) => {
      const matchesSearch =
        patient.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        patient.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        patient.memberId?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus =
        statusFilter === "all" || patient.status === statusFilter;

      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case "name":
          aValue = a.fullName?.toLowerCase() || "";
          bValue = b.fullName?.toLowerCase() || "";
          break;
        case "email":
          aValue = a.email?.toLowerCase() || "";
          bValue = b.email?.toLowerCase() || "";
          break;
        case "memberId":
          aValue = a.memberId?.toLowerCase() || "";
          bValue = b.memberId?.toLowerCase() || "";
          break;
        case "dob":
          aValue = new Date(a.dob || "1900-01-01");
          bValue = new Date(b.dob || "1900-01-01");
          break;
        case "status":
          aValue = a.status || "active";
          bValue = b.status || "active";
          break;
        case "createdAt":
          aValue = new Date(a.createdAt || "1900-01-01");
          bValue = new Date(b.createdAt || "1900-01-01");
          break;
        default:
          aValue = a.fullName?.toLowerCase() || "";
          bValue = b.fullName?.toLowerCase() || "";
      }

      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  // Pagination logic
  const indexOfLastPatient = currentPage * patientsPerPage;
  const indexOfFirstPatient = indexOfLastPatient - patientsPerPage;
  const currentPatients = filteredPatients.slice(
    indexOfFirstPatient,
    indexOfLastPatient
  );
  const totalPages = Math.ceil(filteredPatients.length / patientsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Dashboard Header */}
        <DashboardHeader
          lang={lang}
          onAddPatient={handleAddPatient}
          doctorName="Dr. John Smith"
        />

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-red-800">
                  Patient Addition Error
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  <p className="font-medium">{error}</p>
                  {error.includes("email") && (
                    <div className="mt-2 p-2 bg-red-100 rounded border border-red-200">
                      <p className="text-xs text-red-600">
                        <strong>Tip:</strong> Check if this patient already
                        exists in your patient list, or use a different email
                        address.
                      </p>
                    </div>
                  )}
                </div>
                <div className="mt-4 flex space-x-3">
                  <button
                    onClick={() => setError(null)}
                    className="bg-red-100 px-3 py-2 rounded-md text-sm font-medium text-red-800 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Dismiss
                  </button>
                  <button
                    onClick={loadPatients}
                    className="bg-blue-100 px-3 py-2 rounded-md text-sm font-medium text-blue-800 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Refresh Patient List
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="mb-6 flex justify-center items-center py-12">
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="text-gray-600">Loading patients...</span>
            </div>
          </div>
        )}

        {/* Stats Section */}
        {!loading && <StatsSection patients={patients} lang={lang} />}

        {/* Search and Filters */}
        {!loading && (
          <SearchAndFilters
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            sortBy={sortBy}
            sortOrder={sortOrder}
            showSortDropdown={showSortDropdown}
            handleSort={handleSort}
            handleSortOption={handleSortOption}
            lang={lang}
          />
        )}

        {/* Patient Table */}
        {!loading && (
          <PatientTable
            currentPatients={currentPatients}
            filteredPatients={filteredPatients}
            patientsPerPage={patientsPerPage}
            currentPage={currentPage}
            totalPages={totalPages}
            indexOfFirstPatient={indexOfFirstPatient}
            indexOfLastPatient={indexOfLastPatient}
            handleViewPatient={handleViewPatient}
            handleEditPatient={handleEditPatient}
            handleDeletePatient={handleDeletePatient}
            handleStatusChange={handleStatusChange}
            handlePageChange={handlePageChange}
            isUpdatingStatus={isUpdatingStatus}
            lang={lang}
          />
        )}
      </div>

      {/* Modals */}
      <PatientFormModal
        showModal={showAddPatientModal}
        editingPatient={editingPatient}
        patientForm={patientForm}
        handleFormChange={handleFormChange}
        handleSavePatient={handleSavePatient}
        onClose={() => {
          setShowAddPatientModal(false);
          setValidationErrors({});
          setError(null); // Clear any errors when closing modal
        }}
        resetForm={resetForm}
        validationErrors={validationErrors}
        error={error}
        isSaving={isSaving}
        lang={lang}
      />

      <PatientDetailsModal
        showModal={showPatientDetailsModal}
        selectedPatient={selectedPatient}
        onClose={() => {
          setShowPatientDetailsModal(false);
          setSelectedPatient(null);
        }}
        onEdit={() => {
          setShowPatientDetailsModal(false);
          setSelectedPatient(null);
          handleEditPatient(selectedPatient);
        }}
        lang={lang}
      />

      <DeleteConfirmationModal
        showModal={showDeleteConfirmModal}
        patientToDelete={patientToDelete}
        onClose={() => {
          setShowDeleteConfirmModal(false);
          setPatientToDelete(null);
        }}
        onConfirm={confirmDeletePatient}
        isDeleting={isDeleting}
        lang={lang}
      />
    </div>
  );
};

export default DoctorDashboard;
